import { useState } from 'react';
import Link from 'next/link';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'next-i18next/pages';
import { PackageHotelResult } from 'api/queries/packageTour';
import { formatMoney } from 'utils/packageSearch';
import { PACKAGE_TOURS_IN_HOUSE } from 'utils/packageToursSwitch';
import FavouriteButton from './FavouriteButton';

// WHIT-482 P3: one hotel in the list; its name and button open the hotel
// page (P4) with the same search.

export const Stars: React.FC<{ category: number }> = ({ category }) => {
  const { t } = useTranslation();
  return (
    <span
      className="text-tt-yellow text-sm"
      title={t('{{count}} stars', { count: category })}
      aria-label={t('{{count}} stars', { count: category })}
    >
      {'★'.repeat(Math.floor(category))}
      {category % 1 ? '½' : ''}
    </span>
  );
};

export const distance = (meters: number, language: string) =>
  meters < 1000
    ? `${meters} m`
    : `${new Intl.NumberFormat(language === 'hu' ? 'hu-HU' : 'en-GB', {
        maximumFractionDigits: 1,
      }).format(meters / 1000)} km`;

const HotelCard: React.FC<{
  hotel: PackageHotelResult;
  boardTypeName: (code: string) => string;
  airportName: (id: string) => string;
  // The provider's English country name in the visitor's language.
  countryName?: (name: string) => string;
  // The hotel page, with the search in its query.
  href: { pathname: string; query: Record<string, string> };
  // WHIT-499: on the results list, a flight result's card: no border, an
  // outline on hover, the flight card's padding.
  flat?: boolean;
}> = ({
  hotel,
  boardTypeName,
  airportName,
  countryName = name => name,
  href,
  flat = false,
}) => {
  const { t, i18n } = useTranslation();
  const images = hotel.thumbnails || [];
  const [image, setImage] = useState(0);
  const location = [
    hotel.location?.country && countryName(hotel.location.country),
    hotel.location?.region,
    hotel.location?.resort,
  ]
    .filter(Boolean)
    .join(' » ');
  const leadIn = hotel.leadIn;

  return (
    // Outlined on hover, as a flight result is (WHIT-497).
    <article
      className={`flex flex-col sm:flex-row overflow-hidden rounded-2xl border bg-white hover:border-tt-dark-purple ${
        flat ? 'border-transparent' : 'border-tt-gray-purple'
      }`}
    >
      <div className="relative sm:w-64 shrink-0 bg-tt-verylight-purple aspect-[4/3] sm:aspect-auto">
        {images.length > 0 && (
          // The tour operator's image host is not known in advance, so a
          // plain img rather than next/image (which needs its domain listed).
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={images[image]}
            alt={hotel.name}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
        {images.length > 1 && (
          <>
            <button
              type="button"
              aria-label={t('Previous image')}
              onClick={() =>
                setImage(i => (i - 1 + images.length) % images.length)
              }
              className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/80 text-tt-dark-purple"
            >
              ‹
            </button>
            <button
              type="button"
              aria-label={t('Next image')}
              onClick={() => setImage(i => (i + 1) % images.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/80 text-tt-dark-purple"
            >
              ›
            </button>
          </>
        )}
        {PACKAGE_TOURS_IN_HOUSE && (
          <FavouriteButton
            hotelId={hotel.hotelId}
            name={hotel.name}
            className="absolute right-2 top-2"
          />
        )}
      </div>

      <div className={`flex flex-1 flex-col gap-1 ${flat ? 'p-6' : 'p-4'}`}>
        <div className="flex flex-wrap items-baseline gap-x-2">
          <h2 className="text-lg font-semibold text-tt-dark-purple">
            <Link href={href} className="hover:underline">
              {hotel.name}
            </Link>
          </h2>
          {!!hotel.category && <Stars category={hotel.category} />}
        </div>
        {location && <p className="text-sm text-gray-600">{location}</p>}
        <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {typeof hotel.guestRatingPercent === 'number' && (
            <li>
              <span className="font-semibold">
                {hotel.guestRatingPercent} %
              </span>{' '}
              {t('guest recommendation')}
            </li>
          )}
          {leadIn?.boardType && <li>{boardTypeName(leadIn.boardType)}</li>}
          {leadIn?.transportType === 'FLIGHT' && (
            <li>
              {leadIn.departureAirportId
                ? t('Flight from {{airport}}', {
                    airport: airportName(leadIn.departureAirportId),
                  })
                : t('By plane')}
            </li>
          )}
          {/* WHIT-523: the lead-in offer's tour operator; nothing when the
              provider names none. */}
          {leadIn?.tourOperator?.name && <li>{leadIn.tourOperator.name}</li>}
          {typeof hotel.distanceToBeachMeters === 'number' && (
            <li>
              {t('{{distance}} to the beach', {
                distance: distance(hotel.distanceToBeachMeters, i18n.language),
              })}
            </li>
          )}
        </ul>
        <div className="mt-auto flex flex-wrap items-end justify-end gap-x-4 gap-y-2 pt-3">
          {leadIn?.price && (
            <p className="text-right">
              <span className="text-sm text-gray-600">{t('from')} </span>
              <span className="text-xl font-semibold text-tt-dark-purple">
                {formatMoney(leadIn.price, i18n.language)}
              </span>
              <span className="text-sm text-gray-600"> / {t('person')}</span>
            </p>
          )}
          <Link
            href={href}
            className="rounded-lg bg-tt-dark-purple px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            {t('See offers')}
          </Link>
        </div>
      </div>
    </article>
  );
};

// WHIT-497: the list's placeholder has the card's shape, as on flight
// results: the image on the left, then the name, the facts and the price.
export const HotelCardSkeleton: React.FC = () => (
  <div className="flex flex-col sm:flex-row overflow-hidden rounded-2xl bg-white">
    <div className="sm:w-64 shrink-0 aspect-[4/3] sm:aspect-auto sm:min-h-[12rem] leading-none">
      <Skeleton height="100%" borderRadius={0} />
    </div>
    <div className="flex flex-1 flex-col gap-2 p-4">
      <Skeleton width="60%" height={22} />
      <Skeleton width="40%" />
      <Skeleton width="80%" />
      <div className="mt-auto flex justify-end gap-4 pt-3">
        <Skeleton width={110} height={28} />
        <Skeleton width={96} height={36} borderRadius={8} />
      </div>
    </div>
  </div>
);

export default HotelCard;
